import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Box, TextField, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Button, Checkbox
} from '@mui/material';
import HelpIcon from './HelpIcon';
import CloseIcon from '@mui/icons-material/Close';

const CyclesDialog = ({ open, onClose, bar1Date, bar2Date, onDisplay, onErase, onManualDrawToggle, onOpenVisualUniverse }) => {
  // State for all the form controls
  const [distanceUnit, setDistanceUnit] = useState('days');
  const [displayType, setDisplayType] = useState('fixed_length');
  const [lineWidth, setLineWidth] = useState(3);
  const [color, setColor] = useState('#FF00FF');
  const [fibRatio, setFibRatio] = useState('1.618');
  const [manualDraw, setManualDraw] = useState(false);

  const handleDisplay = () => {
    // Pass the dialog's state up to the parent component
    onDisplay({ distanceUnit, displayType, lineWidth, color, fibRatio });
  };

  const handleErase = () => {
    onErase();
  };

  const handleManualDrawChange = (event) => {
    const isChecked = event.target.checked;
    setManualDraw(isChecked);
    if (onManualDrawToggle) {
      onManualDrawToggle(isChecked, { color, lineWidth });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="cycles-dialog-title"
      sx={{ pointerEvents: open ? 'auto' : 'none' }}
      PaperProps={{
        sx: {
          position: 'fixed',
          top: 80,
          right: 20,
          m: 0,
          width: '350px',
        },
      }}
      BackdropProps={{ style: { backgroundColor: 'transparent' } }}
      disableEnforceFocus
      hideBackdrop
    >
      <DialogTitle sx={{ m: 0, p: 2, cursor: 'move', pointerEvents: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} id="cycles-dialog-title">
        <span>Cycles</span>
        <Box>
          <HelpIcon helpKey="cyclesDialog" />
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
            <TextField
              label="Bar 1 Date"
              value={bar1Date || ''}
              InputProps={{ readOnly: true }}
              sx={{ width: '150px' }}
            />
            <img 
              src="/images/cycle-dlg-chart.png" 
              alt="Bar 1 Chart" 
              style={{ 
                width: '32px', 
                height: '32px', 
                cursor: bar1Date ? 'pointer' : 'not-allowed', 
                opacity: bar1Date ? 1 : 0.5 
              }} 
              onClick={() => bar1Date && onOpenVisualUniverse(bar1Date)}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <TextField
              label="Bar 2 Date"
              value={bar2Date || ''}
              InputProps={{ readOnly: true }}
              sx={{ width: '150px' }}
            />
            <img 
              src="/images/cycle-dlg-chart.png" 
              alt="Bar 2 Chart" 
              style={{ 
                width: '32px', 
                height: '32px', 
                cursor: bar2Date ? 'pointer' : 'not-allowed', 
                opacity: bar2Date ? 1 : 0.5 
              }} 
              onClick={() => bar2Date && onOpenVisualUniverse(bar2Date)}
            />
          </Box>
        </Box>
        <FormControl component="fieldset">
          <FormLabel component="legend">Number Of</FormLabel>
          <RadioGroup row value={distanceUnit} onChange={(e) => setDistanceUnit(e.target.value)}>
            <FormControlLabel value="days" control={<Radio />} label="days" />
            <FormControlLabel value="bars" control={<Radio />} label="bars" />
          </RadioGroup>
        </FormControl>
        <FormControl component="fieldset">
          <FormLabel component="legend">Display cycles</FormLabel>
          <RadioGroup row value={displayType} onChange={(e) => setDisplayType(e.target.value)}>
            <FormControlLabel value="fixed_length" control={<Radio />} label="fixed length" />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel value="fib_ratio_length" control={<Radio />} label="" />
              <TextField 
                value={fibRatio} 
                onChange={(e) => setFibRatio(e.target.value)} 
                size="small" 
                sx={{ width: '80px' }} 
                disabled={displayType !== 'fib_ratio_length'}
              />
            </Box>
          </RadioGroup>
        </FormControl>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Line width"
            type="number"
            value={lineWidth}
            onChange={(e) => setLineWidth(e.target.value)}
            size="small"
            sx={{ width: '100px' }}
          />
          <TextField
            label="Color"
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            size="small"
            sx={{ width: '100px' }}
          />
        </Box>
        <FormControlLabel
          control={<Checkbox checked={manualDraw} onChange={handleManualDrawChange} />}
          label="Draw vertical line on bar click"
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-around', p: 2 }}>
        <Button variant="contained" onClick={handleDisplay} sx={{ flex: 1 }}>Display</Button>
        <Button variant="outlined" onClick={handleErase} sx={{ flex: 1 }}>Erase</Button>
      </DialogActions>
    </Dialog>
  );
};

CyclesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  bar1Date: PropTypes.string,
  bar2Date: PropTypes.string,
  onDisplay: PropTypes.func.isRequired,
  onErase: PropTypes.func.isRequired,
  onManualDrawToggle: PropTypes.func,
  onOpenVisualUniverse: PropTypes.func,
};

export default CyclesDialog;

